import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewContainerRef } from '@angular/core';
import { DialogRef, DialogResult, DialogCloseResult, DialogService } from '@progress/kendo-angular-dialog';
import { SVGIcon, plusIcon, xIcon, copyIcon, pencilIcon, checkIcon } from '@progress/kendo-svg-icons';
import { takeUntil, combineLatest, filter } from 'rxjs';
import { dialogTimer, dialogAnimationDuration } from 'src/app/shared/constants/dialog-specs';
import { DIALOG_MESSAGES } from 'src/app/shared/constants/notification-messages';
import { OperationType } from 'src/app/shared/enums/operation-type';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faUser, faEdit, faTreeCity, faCheck } from '@fortawesome/free-solid-svg-icons';
import { NotificationService } from '@progress/kendo-angular-notification';
import { User } from 'src/app/shared/models/user';
import { AuthStore } from 'src/app/stores/auth.store';
import { ReferenceStore } from 'src/app/stores/reference.store';
import { AbstractComponent } from 'src/app/shared/components/abstract-component';
import { LocationProfileService } from 'src/app/modules/location-profile/services/location-profile.service';
import { IClassData, IClassDataRequest } from 'src/app/modules/location-profile/interfaces/class-data';
import { Permission } from '../../models/permission';
import { Permissions } from '../../enums/permissions';
import _ from 'lodash';

@Component({
  selector: 'app-classes-tab-content',
  templateUrl: './classes-tab-content.component.html',
  styleUrl: './classes-tab-content.component.scss',
  standalone: false
})
export class ClassesTabContentComponent extends AbstractComponent
  implements OnInit, OnChanges {

  @Input() locationCode!: string;
  @Input() buildingCode!: string;
  @Input() schoolAgencySk!: string;
  classesData: IClassData[] = [];
  schoolYearData: IReferenceData[] = [];
  plusIcon: SVGIcon = plusIcon;
  xIcon: SVGIcon = xIcon;
  copyIcon: SVGIcon = copyIcon;
  pencilIcon: SVGIcon = pencilIcon;
  editing = false;
  isButtonDisabled = true;
  referenceData: Record<string, IReferenceData[]> = {};
  schoolYear: string;
  classesLoading = false;
  saveAttempted = false;

  defaultSchoolYrItem: IReferenceData | string;
  gridHeight = 520;

  checkIcon: SVGIcon = checkIcon;
  private classesLoaded = false;
  showSavePopUp: boolean = false;
  private originalClassesData: IClassData[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.classesLoaded &&
      this.locationCode &&
      this.buildingCode &&
      Object.keys(this.referenceData).length
    ) {
      this.classesLoaded = true;
      this.initializeClassesData();
    }
  }

  readonly monthOfJune = 6;
  readonly previousYear = -1;
  readonly nextYear = 1;
  readonly twoYears = 2;
  readonly currentSchoolYearIndex = 0;
  readonly spliceOne = 1;
  readonly previousIndex = -1;

  readonly invalidSaveMessage = 'Your submission contains invalid or duplicate rows. Please review and correct or remove the duplicates before submitting your list of classes.';
  readonly scisClassTypeId = 2;
  readonly specialClassTypeId = 1;
  readonly classesUpdatedMessage = (year: string) =>
    `Changes are successfully updated.`;
  user: User;
  cseChair: boolean;
  cseSW: boolean;
  canAddEditNewClass: boolean;
  canEditOperationalClasses: boolean;
  agenceSk: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private locationProfileService: LocationProfileService,
    private referenceStore: ReferenceStore,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    library: FaIconLibrary,
    private authStore: AuthStore,) {
    library.addIcons(faUser, faEdit, faTreeCity, faCheck);
    super();
    this.authStore.UserInfo$.pipe(filter((user: User) => !!user)).subscribe(
      (userInfo: User) => {
        this.user = userInfo;
        this.canAddEditNewClass = _.some(this.user?.userRoles, role =>
          _.some(role.userPermissions, { permissionId: Permissions.AddEditNewClass })
        );
        this.canEditOperationalClasses = _.some(this.user?.userRoles, role =>
          _.some(role.userPermissions, { permissionId: Permissions.EditOperationalClasses })
        );
      }
    );
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((params: ParamMap) => {
        this.agenceSk = params.get('id') as string;
      })
    this.schoolYearData = this.getSchoolYears();
    this.schoolYear = this.schoolYearData[this.currentSchoolYearIndex]?.id as string;
    this.#getReferenceData();
  }

  deleteClass(index: number): void {
    const dialogRef = this.dialogService.open({
      title: 'Confirm Delete',
      content: 'Are you sure you want to delete?',
      actions: [
        { text: DIALOG_MESSAGES.confirmNo },
        { text: DIALOG_MESSAGES.confirmYes, themeColor: 'primary' },
      ],
      width: 550,
      height: 250,
    });

    dialogRef?.result?.subscribe((result: DialogResult) => {
      if ('text' in result && result.text === 'Yes') {

        const selectedRow = this.filteredClassesData[index];

        if (!selectedRow) {
          return;
        }

        const actualIndex = this.classesData.indexOf(selectedRow);

        if (actualIndex !== -1) {
          this.classesData[actualIndex].isDeleted = true;
        }
      }
    });
  }

  addEmptyRow(): void {
    const newRow: IClassData = {
      operational: 0,
      authorized: 0,
      operationType: OperationType.Add,
    };
    this.classesData = [...this.classesData, newRow];
  }

  duplicateLastRow(): void {
    const newRowData: IClassData = {
      ...this.classesData.at(this.classesData.length + this.previousIndex),
    };
    delete newRowData.classSk;
    newRowData.operationType = OperationType.Add;

    this.classesData = [...this.classesData, newRowData];
  }

  cancel(): void {
    const dialogRef = this.dialogService.open({
      title: 'Confirm Cancel',
      content: 'Are you sure you want to cancel? Any unsaved changes will be lost.',
      actions: [
        { text: DIALOG_MESSAGES.confirmNo },
        { text: DIALOG_MESSAGES.confirmYes, themeColor: 'primary' }
      ],
      width: 550,
      height: 250
    });

  dialogRef.result
    .pipe(takeUntil(this.componentDestroyed))
    .subscribe((result: DialogResult) => {
      if (
        !(result instanceof DialogCloseResult) &&
        result?.text === DIALOG_MESSAGES.confirmYes
      ) {
        this.editing = false;
        this.saveAttempted = false;
        this.initializeClassesData();
      }
    });
  }

  hasChanges(): boolean {
    return (
      this.classesData?.some(
        (classData: IClassData) => this.getOperationType(classData) !== OperationType.None
      ) ?? false
    );
  }

  openDialog(isSave: boolean): void {
    const isCurrentSchoolYear =
      this.schoolYear === this.schoolYearData[this.currentSchoolYearIndex]?.id;

    if (isSave && isCurrentSchoolYear && this.hasChanges()) {
      this.showSavePopUp = true;
    } else {
      const dialog: DialogRef = this.dialogService.open({
        title: DIALOG_MESSAGES.confirmTitle,
        content: DIALOG_MESSAGES.areYouSureMessage,
        actions: [
          { text: DIALOG_MESSAGES.confirmNo },
          { text: DIALOG_MESSAGES.confirmYes, themeColor: 'primary' },
        ],
        width: 550,
        height: 250,
      });

      dialog.result
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((result: DialogResult) => {
          if (!(result instanceof DialogCloseResult)) {
            if (result?.text === DIALOG_MESSAGES.confirmYes) {
              if (isSave) {
                this.saveClasses(false);
              } else {
                this.cancel();
              }
            }
          }
        });
    }
  }

  onCloseDialog(): void {
  this.showSavePopUp = false;
}

saveCurrentSY(): void {
  this.saveClasses(false);
  this.showSavePopUp = false;
}

saveCurrentSYAndNextSY(): void {
  this.saveClasses(true);
  this.showSavePopUp = false;
}

  saveClasses(currentSYorNextSY: boolean): void {
    this.saveAttempted = true;
    this.filteredClassesData.forEach(row => {
      row._touched = {
        programCode: true,
        overallRatio: true,
        seRatio: true,
        language: true,
        dayLength: true,
        authorized: true,
        operational: true
      };
    });
    if (this.classesInvalid()) {
      this.notificationService.show({
        content: this.invalidSaveMessage,
        cssClass: 'notification-error',
        hideAfter: dialogTimer,
        position: { horizontal: 'center', vertical: 'top' },
        animation: { type: 'slide', duration: dialogAnimationDuration },
        type: { style: 'error', icon: true },
      });
    } else {
      const classesDataRequest: IClassDataRequest[] = this.classesData.map(
        (classData: IClassData) => {
          const operationType = this.getOperationType(classData);
          classData.operationType = operationType;
          return {
            ...classData,
            programCode: classData.programCode?.id,
            classType: classData.classType?.id,
            overallRatio: classData.overallRatio?.id,
            seRatio: classData.seRatio?.id,
            language: classData.language?.id,
            dayLength: classData.dayLength?.id,
            twomonth: classData.twoMonth,
            tenmonth: classData.tenMonth,
            operationType,
            schoolYear: this.schoolYear,
            locationCode: this.locationCode,
            buildingCode: this.buildingCode,
            agencysk: this.schoolAgencySk,
            applyToBothYear: currentSYorNextSY,
            specializedProgram: classData.specializedProgram?.id
          };
        }
      );
      this.locationProfileService
        .submitClassesData(classesDataRequest)
        .subscribe((res: ApiResponse<IClassDataRequest>) => {
          if (res.error) {
            this.notificationService.show({
              content: res.error,
              cssClass: 'notification-error',
              hideAfter: dialogTimer,
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'slide', duration: dialogAnimationDuration },
              type: { style: 'error', icon: true },
            });
            this.classesData.forEach(
              (classData: IClassData) => (classData.isDeleted = false)
            );
          } else {
            this.notificationService.show({
              content: this.classesUpdatedMessage(this.schoolYear),
              cssClass: 'notification-success',
              hideAfter: dialogTimer,
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'slide', duration: dialogAnimationDuration },
              type: { style: 'success', icon: true },
            });
            this.saveAttempted = false;
            this.editing = false;
            this.initializeClassesData();
          }
        });
    }
  }

  navigateToClass(data: IClassData): void {
    if (this.agenceSk) {
      this.router.navigate([
        `/location/class/${this.locationCode}/${this.buildingCode}/${this.schoolYear}/${data.classSk}/${this.agenceSk}`,
      ]);
    }
    else {
      this.router.navigate([
        `/location/class/${this.locationCode}/${this.buildingCode}/${this.schoolYear}/${data.classSk}`,
      ]);
    }
  }

  classesInvalid(): boolean {
    const existingClassesTwoMo: Set<string> = new Set<string>();
    const existingClassesTenMo: Set<string> = new Set<string>();

    for (const classData of this.filteredClassesData) {
      classData.duplicate = false;
      const programCode = (classData?.programCode as IReferenceData)?.id;
      const language = (classData?.language as IReferenceData)?.id;
      const dayLength = (classData?.dayLength as IReferenceData)?.id;
      const hasRequiredFields = [
        classData.programCode,
        classData.classType,
        classData.language,
        classData.dayLength,
        classData.authorized,
        classData.operational,
      ].every((value) => value != null);

      const hasRequiredRatio =
  classData.classType?.id === this.specialClassTypeId
    ? !!classData.seRatio
    : classData.classType?.id === this.scisClassTypeId
      ? !!classData.overallRatio
      : true;

      if (!hasRequiredFields || !hasRequiredRatio) {
        classData.duplicate = true;
        continue;
      }
      // Don't check duplicates until all 3 values are present
      if (!programCode || !language || !dayLength) {
        classData.duplicate = true; //invalid row
        continue;
      }

      const key = `${programCode}+${language}+${dayLength}`;
      if (classData.twoMonth) {
        classData.duplicate = existingClassesTwoMo.has(key);
        existingClassesTwoMo.add(key);
      }

      if (classData.tenMonth) {
        classData.duplicate = classData.duplicate || existingClassesTenMo.has(key);
        existingClassesTenMo.add(key);
      }
    }
    return this.filteredClassesData.some(
      (classData: IClassData) => classData.duplicate
    );
  }
  get canEdit(): boolean {
    return !this.router.url.includes('edit');
  }
  initializeClassesData(): void {
    this.classesLoading = true;
    this.locationProfileService
      .getClassesData(this.locationCode, this.buildingCode, this.schoolYear)
      .subscribe((res: IClassData[]) => {
        res?.forEach((classData: IClassData) => {
          classData.classType = this.referenceData?.classType?.find(
            (classType: IReferenceData) =>
              Number(classType.id) === Number(classData.classType)
          );
          classData.dayLength = this.referenceData?.dayType?.find(
            (dayType: IReferenceData) =>
              Number(dayType.id) === Number(classData.dayLength)
          );
          classData.language = this.referenceData?.language?.find(
            (language: IReferenceData) =>
              Number(language.id) === Number(classData.language)
          );
          classData.overallRatio = this.referenceData?.overallRatio?.find(
            (overallRatio: IReferenceData) =>
              Number(overallRatio.id) === Number(classData.overallRatio)
          );
          classData.programCode = this.referenceData?.programCode?.find(
            (programCode: IReferenceData) =>
              Number(programCode.id) === Number(classData.programCode)
          );
          classData.seRatio = this.referenceData?.seRatio?.find(
            (seRatio: IReferenceData) =>
              Number(seRatio.id) === Number(classData.seRatio)
          );
          classData.specializedProgram = this.referenceData?.specializedProgram?.find(
            (specializedProgram: IReferenceData) =>
              Number(specializedProgram.id) === Number(classData.specializedProgram)
          );
          classData.twoMonth = classData.twomonth;
          classData.tenMonth = classData.tenmonth;
          classData.operationType = OperationType.None;
        });
        this.classesData = res;
        this.originalClassesData = _.cloneDeep(res);
        this.classesLoading = false;
      });
  }

  getOperationType(classData: IClassData): number {
    if (classData.isDeleted) return OperationType.Delete;
    if (!classData.classSk) return OperationType.Add;
    const orig = this.originalClassesData?.find((x) => x.classSk === classData.classSk);
    const hasChanged =
      !orig ||
      orig.programCode?.id != classData.programCode?.id ||
      orig.overallRatio?.id != classData.overallRatio?.id ||
      orig.seRatio?.id != classData.seRatio?.id ||
      orig.language?.id != classData.language?.id ||
      orig.dayLength?.id != classData.dayLength?.id ||
      orig.authorized != classData.authorized ||
      orig.operational != classData.operational ||
      orig.specializedProgram?.id != classData.specializedProgram?.id ||
      Boolean(orig.twoMonth) !== Boolean(classData.twoMonth) ||
      Boolean(orig.tenMonth) !== Boolean(classData.tenMonth);

    return hasChanged ? OperationType.Edit : OperationType.None;
  }

  #getReferenceData(): void {
    combineLatest<IReferenceData[][]>([
      this.referenceStore.programCode$,
      this.referenceStore.classType$,
      this.referenceStore.overallRatio$,
      this.referenceStore.seRatio$,
      this.referenceStore.dayType$,
      this.referenceStore.language$,
      this.referenceStore.schoolYears$,
      this.referenceStore.specializedProgram$,
    ]).subscribe(
      ([
        programCode,
        classType,
        overallRatio,
        seRatio,
        dayType,
        language,
        schoolYears,
        specializedProgram,
      ]: IReferenceData[][]) => {
        this.referenceData.programCode = programCode;
        this.referenceData.classType = classType;
        this.referenceData.overallRatio = overallRatio;
        this.referenceData.seRatio = seRatio;
        this.referenceData.dayType = dayType;
        this.referenceData.language = language;
        this.referenceData.schoolYears = schoolYears;
        this.referenceData.specializedProgram = specializedProgram;
        if (this.locationCode && this.buildingCode) {
          this.initializeClassesData();
        }
      }
    );
  }

  get filteredClassesData(): IClassData[] {
    return this.classesData?.filter(
      (classData: IClassData) => !classData.isDeleted
    );
  }

  public rowCallback: (context: {
    dataItem: { duplicate: boolean };
  }) => Record<string, boolean> = (context: {
    dataItem: { duplicate: boolean };
  }) => {
      if (this.saveAttempted) {
        this.classesInvalid();
      }
      return {
        'duplicate-row': context.dataItem.duplicate,
      };
    };

  getSchoolYears(): IReferenceData[] {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;

    // School year starts in July
    const currentSchoolYearStart = month >= 7 ? year : year - 1;

    const currentYear = { id: `${currentSchoolYearStart}-${currentSchoolYearStart + 1}`, description: `(Current) ${currentSchoolYearStart}-${currentSchoolYearStart + 1}` };

    const upcomingYear = { id: `${currentSchoolYearStart + 1}-${currentSchoolYearStart + 2}`, description: `(Upcoming) ${currentSchoolYearStart + 1}-${currentSchoolYearStart + 2}` };

    return [
      currentYear,
      upcomingYear
    ];
  }

  showView(data): boolean {
    const isValid = !!data &&
      data.programCode != null &&
      data.operational != null &&
      data.dayLength != null &&
      (data.tenmonth || data.twomonth) &&
      (
        (data.classType?.id === 1 && data.seRatio != null) ||
        (data.classType?.id === 2 && data.overallRatio != null) ||
        (data.classType?.id !== 1 && data.classType?.id !== 2)
      );

    return isValid;
  }

  onProgramCodeChange(programCode: IReferenceData, data: IClassData): void {
    data.classType = this.referenceData?.classType?.find(
      (item: IReferenceData) => item.description === programCode?.value
    );
    if (data.classType?.id === 2) {
      data.seRatio = null;
    } else {
      data.overallRatio = null;
    }
  }

  isClassRowDisabled(data: any): boolean {
    if (!data || data.placedCount == null) {
      return false;
    }
    return Number(data?.placedCount) > 0;
  }

  markTouched(row: any, field: string): void {
    row._touched ??= {};
    row._touched[field] = true;
  }

  showError(row: any, field: string): boolean {
    return row._touched?.[field] && row[field] == null;
  }

  getUpcomingSchoolYear(): string {
    const [startYear, endYear] = this.schoolYear.split('-').map(Number);
    return `${startYear + 1}-${endYear + 1}`;
  }

  override afterOnDestroy(): void {
    // On Destroy
  }
}
